import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { toast } from 'sonner';
import { MapPin, RefreshCw } from 'lucide-react';
import ClientsMap from '@/components/ClientsMap';
import { geocodeAddress, sleep } from '@/lib/geocode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Carte() {
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [villeFilter, setVilleFilter] = useState('all');
  const [geocoding, setGeocoding] = useState(false);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, getAuthHeaders());
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const hasAddress = (c) => (c.adresse || c.code_postal || c.ville);
  const isGeocoded = (c) => c.latitude !== null && c.latitude !== undefined;

  const withAddress = clients.filter(hasAddress);
  const geocodedClients = clients.filter(isGeocoded);
  const missingCount = withAddress.filter((c) => !isGeocoded(c)).length;

  const villeOptions = React.useMemo(() => {
    const set = new Set();
    geocodedClients.forEach((c) => {
      if (c.ville && c.ville.trim()) set.add(c.ville.trim());
    });
    const opts = [{ value: 'all', label: 'Toutes les villes' }];
    Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .forEach((v) => opts.push({ value: v.toLowerCase(), label: v }));
    return opts;
  }, [geocodedClients]);

  const displayed = villeFilter === 'all'
    ? geocodedClients
    : geocodedClients.filter((c) => (c.ville || '').trim().toLowerCase() === villeFilter);

  const handleGeocode = async () => {
    const missing = withAddress.filter((c) => !isGeocoded(c));
    if (missing.length === 0) return;
    setGeocoding(true);
    let done = 0;
    let failed = 0;
    for (const c of missing) {
      try {
        const coords = await geocodeAddress(c);
        if (coords) {
          await axios.put(`${API_URL}/api/clients/${c.id}/coordinates`, coords, getAuthHeaders());
          done += 1;
        } else {
          failed += 1;
        }
      } catch (e) {
        failed += 1;
      }
      // Respect de la politique Nominatim (max ~1 requête/seconde)
      await sleep(1100);
    }
    await fetchClients();
    setGeocoding(false);
    if (done > 0 && failed === 0) {
      toast.success(`${done} client·e·s géolocalisé·e·s`);
    } else if (done > 0) {
      toast.success(`${done} géolocalisé·e·s, ${failed} adresse(s) introuvable(s)`);
    } else {
      toast.error(`Aucune adresse n'a pu être localisée (${failed} introuvable(s)). Vérifiez l'orthographe et le code postal.`);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="carte-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Carte</h1>
          <p className="text-muted-foreground mt-2">
            Visualisez vos client·e·s sur la carte pour organiser vos déplacements
          </p>
        </div>
        {withAddress.length > 0 && (
          <div className="w-full sm:w-64" data-testid="ville-filter">
            <Combobox
              options={villeOptions}
              value={villeFilter}
              onValueChange={(value) => setVilleFilter(value || 'all')}
              placeholder="Filtrer par ville"
              searchPlaceholder="Rechercher une ville..."
              emptyText="Aucune ville"
            />
          </div>
        )}
      </div>

      {missingCount > 0 && (
        <Card className="rounded-xl border-border/60 bg-amber-50/60" data-testid="geocode-banner">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <p className="text-sm text-amber-900">
              {missingCount} client·e·s ont une adresse mais ne sont pas encore localisé·e·s sur la carte.
            </p>
            <Button onClick={handleGeocode} disabled={geocoding} data-testid="btn-geocode" className="shrink-0">
              <RefreshCw className={`w-4 h-4 mr-2 ${geocoding ? 'animate-spin' : ''}`} />
              {geocoding ? 'Géolocalisation…' : 'Géolocaliser'}
            </Button>
          </CardContent>
        </Card>
      )}

      {withAddress.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MapPin className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground text-center">
              Aucune adresse renseignée. Ajoutez une adresse à vos fiches client·e·s pour les voir sur la carte.
            </p>
          </CardContent>
        </Card>
      ) : (
        <ClientsMap clients={displayed} height="600px" />
      )}
    </div>
  );
}
